import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';

/**
 * KS Digital-tema for Backstage.
 *
 * Fargeverdiene er hentet ordrett fra KS Digitals designsystem
 * (https://design.ksdigital.no, repo ks-no/designsystem), tema «ksdigital»:
 *   packages/themes/src/themes/ksdigital/color-scheme/{light,dark}.css
 *
 * Grunnfarger: accent #00042E (mørkeblå), support1 #714EEA (lilla),
 * neutral #1D252D. Font Inter, hjørneradius 8 px.
 *
 * Backstage bruker Material UI-temaer, så tokens mappes manuelt hit i stedet
 * for å laste CSS-pakken @ks-digital/designsystem-themes. Oppdater verdiene her
 * når designsystemet endrer seg.
 */

/** Lilla er lik i begge moduser og brukes bl.a. av logoen. */
export const KS_PURPLE = '#714eea';

type SchemeTokens = {
  accentBase: string;
  accentBaseHover: string;
  accentBackground: string;
  accentBackgroundTinted: string;
  accentTextDefault: string;
  support1Base: string;
  neutralBackground: string;
  neutralBackgroundTinted: string;
  neutralSurface: string;
  neutralTextDefault: string;
  neutralTextSubtle: string;
  neutralBorderSubtle: string;
  neutralBorderDefault: string;
  /** Lenkefarge: support1 text-subtle, lilla som er lesbar på bakgrunnen. */
  link: string;
};

const light: SchemeTokens = {
  accentBase: '#00042e', // --ds-color-accent-base-default
  accentBaseHover: '#151940', // --ds-color-accent-base-hover
  accentBackground: '#ffffff', // --ds-color-accent-background-default
  accentBackgroundTinted: '#f3f3f5', // --ds-color-accent-background-tinted
  accentTextDefault: '#25284c', // --ds-color-accent-text-default
  support1Base: KS_PURPLE, // --ds-color-support1-base-default
  neutralBackground: '#ffffff', // --ds-color-neutral-background-default
  neutralBackgroundTinted: '#f3f4f4', // --ds-color-neutral-background-tinted
  neutralSurface: '#ffffff', // --ds-color-neutral-surface-default
  neutralTextDefault: '#242c34', // --ds-color-neutral-text-default
  neutralTextSubtle: '#5c6268', // --ds-color-neutral-text-subtle
  neutralBorderSubtle: '#b9bcbe', // --ds-color-neutral-border-subtle
  neutralBorderDefault: '#7a7f84', // --ds-color-neutral-border-default
  link: '#6646d3', // --ds-color-support1-text-subtle
};

const dark: SchemeTokens = {
  accentBase: '#a9aab8', // --ds-color-accent-base-default
  accentBaseHover: '#9395a7', // --ds-color-accent-base-hover
  accentBackground: '#13173e', // --ds-color-accent-background-default
  accentBackgroundTinted: '#1c2045', // --ds-color-accent-background-tinted
  accentTextDefault: '#e4e4e9', // --ds-color-accent-text-default
  support1Base: '#896cee', // --ds-color-support1-base-default
  neutralBackground: '#161c22', // --ds-color-neutral-background-default
  neutralBackgroundTinted: '#1c242c', // --ds-color-neutral-background-tinted
  neutralSurface: '#252d34', // --ds-color-neutral-surface-default
  neutralTextDefault: '#e4e5e6', // --ds-color-neutral-text-default
  neutralTextSubtle: '#9fa2a6', // --ds-color-neutral-text-subtle
  neutralBorderSubtle: '#4b5258', // --ds-color-neutral-border-subtle
  neutralBorderDefault: '#777c81', // --ds-color-neutral-border-default
  link: '#a994f2', // --ds-color-support1-text-subtle
};

const fontFamily = '"Inter", "Helvetica Neue", Arial, sans-serif';

/** Samme rolige gradient (mørkeblå → lilla) på alle sidetyper. */
const pageTheme = (() => {
  const theme = genPageTheme({
    colors: ['#00042e', KS_PURPLE],
    shape: shapes.wave,
  });
  return {
    home: theme,
    documentation: theme,
    tool: theme,
    service: theme,
    website: theme,
    library: theme,
    other: theme,
    app: theme,
    apis: theme,
  };
})();

function createKsTheme(mode: 'light' | 'dark', t: SchemeTokens): UnifiedTheme {
  const base = mode === 'light' ? palettes.light : palettes.dark;
  // Sidepanelet er mørkeblått i begge moduser (beslutning: tydelig merkevare).
  // Lys modus: accent base (#00042e). Mørk modus: accent background (#13173e),
  // som er designsystemets mørkeblå flate for mørk modus – bevisst ikke #00042e,
  // slik at panelet skiller seg fra sidebakgrunnen (#161c22) uten å bli svart.
  const navBackground =
    mode === 'light' ? light.accentBase : dark.accentBackground;
  // Hover og undermeny bruker designsystemets mørk-modus-flater for accent, siden
  // panelet er mørkt i begge moduser. Tidligere verdier (#151940/#1c2045) ga
  // kontrast på bare ca. 1.1 mot panelet, så hover var nesten usynlig.
  const navHover = '#36395a'; // --ds-color-accent-surface-hover (mørk)
  const navSubmenu = '#25294d'; // --ds-color-accent-surface-default (mørk)

  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...base,
        primary: { main: t.accentBase },
        secondary: { main: t.support1Base },
        background: {
          default: t.neutralBackground,
          paper: t.neutralSurface,
        },
        text: {
          primary: t.neutralTextDefault,
          secondary: t.neutralTextSubtle,
        },
        border: t.neutralBorderSubtle,
        textSubtle: t.neutralTextSubtle,
        link: t.link,
        linkHover: t.support1Base,
        navigation: {
          ...base.navigation,
          background: navBackground,
          indicator: t.support1Base,
          color: '#e4e4e9',
          selectedColor: '#ffffff',
          navItem: { hoverBackground: navHover },
          submenu: { background: navSubmenu },
        },
      },
    }),
    fontFamily,
    defaultPageTheme: 'home',
    pageTheme,
    components: {
      BackstageHeader: {
        styleOverrides: {
          header: {
            boxShadow: 'none',
            borderBottom: `4px solid ${t.support1Base}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, textTransform: 'none' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 8 },
        },
      },
    },
  });
}

export const ksDigitalLightTheme = createKsTheme('light', light);
export const ksDigitalDarkTheme = createKsTheme('dark', dark);
